import { supabase } from './supabase';

export interface Agent {
  id: string
  owner_user_id: string
  name: string
  description: string | null
  instruction: string
  model: string
  color: string
  temperature?: number
  max_tokens?: number
  thinking_budget?: number
  language?: string[]
  status: 'active' | 'inactive' | 'archived'
  visibility: 'private' | 'shared' | 'link' | 'public'
  created_at: string
  updated_at: string
  sharable?: boolean
  whatsappConfig?: WhatsAppConfig;
  telegramConfig?: TelegramConfig;
  apiConfig?: ApiConfig;
}

export interface AgentTool {
  id: string
  agent_id: string
  tool_id: string
  tool_prompt: string
  description?: string
  type?: string
  config: Record<string, any>
  created_at: string
  nango_connection_id?: string | null
}

export interface AvailableTool {
  id: string;
  name: string;
  description: string | null;
  config: Record<string, any> | null;
  disabled: boolean | null;
  category: string | null;
  oauth_provider: string | null;
}

export interface UserConnection {
  id: string;
  user_id: string;
  oauth_provider: string;
  connection_id: string;
  account_identifier: string | null;
  created_at: string;
}

export interface WhatsAppConfig {
  enabled: boolean
  twilioAccountSid?: string
  twilioAuthToken?: string
  phoneNumber?: string
  whitelistedNumbers: string[]
  unauthorizedMessage?: string
}

export interface TelegramConfig {
  enabled: boolean
  botToken?: string
  whitelistedUsers: string[]
  unauthorizedMessage?: string
}

export interface ApiConfig {
  enabled: boolean
  apiKey?: string
  apiSecret?: string
}

export interface KnowledgeBaseFile {
  id?: string;
  fileName: string;
  content: string;
}

export interface CreateAgentData {
  name: string
  description?: string
  instruction: string
  model: string
  color: string
  temperature?: number
  max_tokens?: number
  thinking_budget?: number
  language?: string[]
  whatsappConfig?: WhatsAppConfig
  telegramConfig?: TelegramConfig
  apiConfig?: ApiConfig
  sharable?: boolean
  knowledgeBase?: KnowledgeBaseFile[]
  tools?: {
    tool_id: string
    tool_prompt: string
    description?: string
    type?: string
    config?: Record<string, any> | null
    nango_connection_id?: string | null
  }[]
}

// Get all agents for current user
export async function getUserAgents(): Promise<Agent[]> {
  console.log("getUserAgents: Function called");
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    console.error("getUserAgents: User not authenticated. Cannot fetch agents.", userError);
    return [];
  }

  const userId = userData.user.id;
  console.log(`getUserAgents: Fetching agents for userId: ${userId}`);

  // Get agents owned by the user
  const { data: ownedAgents, error: ownedError } = await supabase
    .from('agents')
    .select('*')
    .eq('owner_user_id', userId)
    .order('created_at', { ascending: false });

  if (ownedError) {
    console.error("getUserAgents: Error fetching owned agents:", ownedError);
    throw ownedError;
  }
  console.log("getUserAgents: Fetched owned agents:", ownedAgents);


  // First get the agent IDs that are shared with the user
  console.log("getUserAgents: Fetching shared agent IDs");
  const { data: sharedAgentIds, error: sharingError } = await supabase
    .from('agent_sharing')
    .select('agent_id')
    .eq('user_id', userId);

  if (sharingError) {
    console.error("getUserAgents: Error fetching shared agent IDs:", sharingError);
    // Don't throw here, just log the error and continue with owned agents
    console.warn("getUserAgents: Continuing with owned agents only");
    return ownedAgents || [];
  }
  console.log("getUserAgents: Fetched shared agent IDs:", sharedAgentIds);


  let sharedAgents: Agent[] = [];
  if (sharedAgentIds && sharedAgentIds.length > 0) {
    console.log("getUserAgents: Fetching details for shared agents");
    // Get the actual agent data for shared agents
    const agentIds = sharedAgentIds.map(item => item.agent_id);
    const { data, error: sharedError } = await supabase
      .from('agents')
      .select('*')
      .in('id', agentIds)
      .order('created_at', { ascending: false });

    if (sharedError) {
      console.error("getUserAgents: Error fetching shared agents details:", sharedError);
      console.warn("getUserAgents: Continuing with owned agents only");
    } else {
      sharedAgents = data || [];
      console.log("getUserAgents: Fetched shared agents details:", sharedAgents);
    }
  }

  // Combine owned and shared agents, removing duplicates
  const allAgents = [...(ownedAgents || []), ...sharedAgents];
  const uniqueAgents = allAgents.filter((agent, index, self) => 
    index === self.findIndex(a => a.id === agent.id)
  );
  console.log("getUserAgents: Combined unique agents:", uniqueAgents);


  // Sort the final combined list by created_at descending to ensure latest first
  const sortedAgents = uniqueAgents.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  console.log("getUserAgents: Sorted final agents list:", sortedAgents);
  return sortedAgents;
}

// Get a specific agent
export async function getAgent(id: string): Promise<Agent | null> {
  const { data, error } = await supabase
    .from('agents')
    .select('*, whatsappConfig, telegramConfig, apiConfig')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    throw error
  }
  return data
}

// Get tools for an agent
export async function getAgentTools(agentId: string): Promise<any[]> {
  const { data, error } = await supabase
    .from('agent_tools')
    .select(`
      *,
      available_tools (
        name,
        description
      )
    `)
    .eq('agent_id', agentId)
    .order('created_at')

  if (error) throw error
  return data || []
}

// Create a new agent
export async function createAgent(agentData: CreateAgentData): Promise<Agent> {
  const { data: userData, error: userError } = await supabase.auth.getUser()
  if (userError || !userData.user) throw new Error('User not authenticated')

  const { tools, knowledgeBase, ...restOfAgentData } = agentData;

  // Create the agent
  const { data: agent, error: agentError } = await supabase
    .from('agents')
    .insert({
      owner_user_id: userData.user.id,
      ...restOfAgentData,
    })
    .select()
    .single()

  if (agentError) throw agentError

  // Add tools if provided
  if (tools && tools.length > 0) {
    const toolsToInsert = tools.map(tool => ({
      agent_id: agent.id,
      tool_id: tool.tool_id,
      tool_prompt: tool.tool_prompt,
      type: tool.type,
      config: tool.config,
      nango_connection_id: tool.nango_connection_id
    }))

    const { error: toolsError } = await supabase
      .from('agent_tools')
      .insert(toolsToInsert)

    if (toolsError) {
      // Clean up the agent if tools creation failed
      await supabase.from('agents').delete().eq('id', agent.id)
      throw toolsError
    }
  }

  // Add knowledge base if provided
  if (knowledgeBase && knowledgeBase.length > 0) {
    try {
      await updateAgentKnowledgeBase(agent.id, knowledgeBase);
    } catch (kbError) {
      // Clean up the agent if kb creation failed
      await supabase.from('agents').delete().eq('id', agent.id)
      throw kbError;
    }
  }

  return agent
}

// Update an agent
export async function updateAgent(id: string, updates: Partial<CreateAgentData>): Promise<Agent> {
  const { tools, whatsappConfig, telegramConfig, apiConfig, knowledgeBase, ...agentUpdates } = updates;

  const { data, error } = await supabase
    .from('agents')
    .update({
      ...agentUpdates,
      whatsappConfig: whatsappConfig,
      telegramConfig: telegramConfig,
      apiConfig: apiConfig,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error

  // Update tools if provided
  if (tools) {
    // First, remove all existing tools for this agent
    const { error: deleteError } = await supabase
      .from('agent_tools')
      .delete()
      .eq('agent_id', id);

    if (deleteError) {
      console.error('Error deleting old tools:', deleteError);
      throw deleteError;
    }

    // Then, insert the new tools
    if (tools.length > 0) {
      const toolsToInsert = tools.map(tool => ({
        agent_id: id,
        tool_id: tool.tool_id,
        tool_prompt: tool.tool_prompt,
        type: tool.type,
        config: tool.config,
        nango_connection_id: tool.nango_connection_id
      }));

      const { error: insertError } = await supabase
        .from('agent_tools')
        .insert(toolsToInsert);

      if (insertError) {
        console.error('Error inserting new tools:', insertError);
        throw insertError;
      }
    }
  }

  // Update knowledge base if provided
  if (knowledgeBase) {
    await updateAgentKnowledgeBase(id, knowledgeBase);
  }

  return data
}

// Delete an agent
export async function deleteAgent(id: string): Promise<void> {
  const { error } = await supabase
    .from('agents')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Add tool to agent
export async function addAgentTool(agentId: string, tool: Omit<AgentTool, 'id' | 'agent_id' | 'created_at'>): Promise<AgentTool> {
  const { data, error } = await supabase
    .from('agent_tools')
    .insert({
      agent_id: agentId,
      ...tool
    })
    .select()
    .single()

  if (error) throw error
  return data
}

// Remove tool from agent
export async function removeAgentTool(toolId: string): Promise<void> {
  const { error } = await supabase
    .from('agent_tools')
    .delete()
    .eq('id', toolId)

  if (error) throw error
}

// Get all available tools
export async function getAvailableTools(): Promise<AvailableTool[]> {
  const { data, error } = await supabase
    .from('available_tools')
    .select('id, name, description, config, disabled, category, oauth_provider')
    .eq('disabled', false)
    .order('name');

  if (error) {
    console.error('Error fetching available tools:', error);
    throw error;
  }

  return data || [];
}

// Get only the required user-level OAuth providers for a given agent
export async function getRequiredUserProvidersForAgent(agentId: string): Promise<{ name: string; provider: string }[]> {
  // Step 1: Fetch all tools for the agent that have an oauth_provider.
  const { data, error } = await supabase
    .from('agent_tools')
    .select('config, available_tools!inner(name, oauth_provider)')
    .eq('agent_id', agentId)
    .not('available_tools.oauth_provider', 'is', null);

  if (error) {
    console.error('Error fetching tools for agent:', error);
    throw error;
  }

  if (!data) {
    return [];
  }

  // Step 2: Filter in JavaScript for tools that are NOT using agent-level auth.
  const userLoginTools = data.filter(item => item.config?.login_type !== 'agent');

  // Step 3: Process the filtered results to get a distinct list of providers.
  const providerMap = new Map<string, string>();
  userLoginTools.forEach((item: any) => {
    const tool = item.available_tools;
    if (tool && tool.oauth_provider && !providerMap.has(tool.oauth_provider)) {
      providerMap.set(tool.oauth_provider, tool.name);
    }
  });

  return Array.from(providerMap, ([provider, name]) => ({ name, provider }));
}

// --- User OAuth Connections ---

export async function getUserConnections(userId: string): Promise<UserConnection[]> {
  const { data, error } = await supabase
    .from('user_connections')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user connections:', error);
    throw error;
  }

  return data || [];
}

export async function addUserConnection(userId: string, providerConfigKey: string, connectionId: string): Promise<UserConnection> {
  const { data, error } = await supabase
    .from('user_connections')
    .insert({
      user_id: userId,
      oauth_provider: providerConfigKey,
      connection_id: connectionId,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding user connection:', error);
    throw error;
  }
  return data;
}

// --- Knowledge Base ---
export async function getAgentKnowledgeBase(agentId: string): Promise<KnowledgeBaseFile[]> {
    const { data, error } = await supabase
      .from('agent_kb')
      .select('file_name, content')
      .eq('agent_id', agentId);
  
    if (error) {
      console.error('Error fetching knowledge base:', error);
      throw error;
    }
  
    return (data || []).map(item => ({
      id: `${agentId}-${item.file_name}`, // Create a temporary unique ID
      fileName: item.file_name,
      content: item.content,
    }));
}

export async function updateAgentKnowledgeBase(agentId: string, files: KnowledgeBaseFile[]): Promise<void> {
  // First, remove all existing knowledge base files for this agent
  const { error: deleteError } = await supabase
    .from('agent_kb')
    .delete()
    .eq('agent_id', agentId);

  if (deleteError) {
    console.error('Error deleting old knowledge base files:', deleteError);
    throw deleteError;
  }

  // Then, insert the new knowledge base files
  if (files.length > 0) {
    const filesToInsert = files.map(file => ({
      agent_id: agentId,
      file_name: file.fileName,
      content: file.content,
    }));

    const { error: insertError } = await supabase
      .from('agent_kb')
      .insert(filesToInsert);

    if (insertError) {
      console.error('Error inserting new knowledge base files:', insertError);
      throw insertError;
    }
  }
}


// --- Chat History ---

export interface Session {
  id: string;
  user_id: string;
  app_name: string;
  update_time: string;
  // agent metadata can be added here
}

export interface ChatEvent {
  id: string;
  session_id: string;
  user_id: string;
  invocation_id: string;
  timestamp: string;
  author: string;
  content: {
    role: 'user' | 'model';
    parts: Array<{
      text?: string;
      thought?: boolean;
      function_call?: any;
      function_response?: any;
      file_data?: any;
      inline_data?: any;
    }>;
  };
  partial: boolean;
  turn_complete: boolean;
  error_code?: string;
  error_message?: string;
  interrupted?: boolean;
}

/**
 * Fetches all chat sessions for a given user, optionally filtered by agent.
 * @param userId The UUID of the authenticated user.
 * @param agentId Optional UUID of the agent to filter sessions for.
 * @returns A promise that resolves to an array of sessions.
 */
export async function getSessions(userId: string, agentId?: string): Promise<Session[]> {
  if (!agentId) {
    // Fallback for when agentId is not provided, though the UI always seems to provide it.
    // This part can be adjusted based on desired behavior when no agent is specified.
    const { data, error } = await supabase
      .from('sessions')
      .select('id, user_id, app_name, update_time')
      .eq('user_id', userId)
      .order('update_time', { ascending: false });

    if (error) {
      console.error('Error fetching sessions without agentId:', error);
      throw error;
    }
    return data || [];
  }

  // Call the PostgreSQL function
  const { data, error } = await supabase.rpc('get_sessions_ordered_by_last_message', {
    p_user_id: userId,
    p_agent_id: agentId
  });

  if (error) {
    console.error('Error fetching ordered sessions via RPC:', error);
    throw error;
  }

  return data || [];
}

/**
 * Fetches the event history for a specific chat session.
 * @param sessionId The ID of the session to fetch history for.
 * @param userId The UUID of the authenticated user for RLS.
 * @param limit The number of events to fetch.
 * @param beforeTimestamp A cursor for pagination, fetching events older than this timestamp.
 * @returns A promise that resolves to an array of chat events.
 */
export async function getChatHistory(
  sessionId: string,
  userId: string,
  limit: number = 50,
  beforeTimestamp?: string
): Promise<ChatEvent[]> {
  let query = supabase
    .from('events')
    .select('*')
    .eq('session_id', sessionId)
    .eq('user_id', userId)
    .order('timestamp', { ascending: false })
    .limit(limit);

  if (beforeTimestamp) {
    query = query.lt('timestamp', beforeTimestamp);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching chat history:', error);
    throw error;
  }

  // Reverse to return in chronological order for UI display
  return (data || []).reverse();
}

export const checkAgentSharing = async (agentId: string, userId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('agent_sharing')
    .select('id')
    .eq('agent_id', agentId)
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error checking agent sharing:', error);
    throw error;
  }

  return !!data;
};

export const addAgentSharing = async (agentId: string, userId: string, userEmail: string) => {
  const { data, error } = await supabase
    .from('agent_sharing')
    .insert([{ agent_id: agentId, user_id: userId, user_email: userEmail }])
    .select();

  if (error) {
    console.error('Error adding agent sharing:', error);
    throw error;
  }

  return data;
};

export const removeAgentSharing = async (sharingId: string) => {
  const { error } = await supabase
    .from('agent_sharing')
    .delete()
    .eq('id', sharingId);

  if (error) {
    console.error('Error removing agent sharing:', error);
    throw error;
  }
};


export const getSharedUsersForAgent = async (agentId: string) => {
  const { data, error } = await supabase
    .from('agent_sharing')
    .select('id, user_id, user_email')
    .eq('agent_id', agentId);

  if (error) {
    console.error('Error fetching shared users:', error);
    throw error;
  }

  return data || [];
};

// --- Survey ---

export async function checkSurveyCompletion(userId: string, surveyId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('surveys')
    .select('id')
    .eq('user_id', userId)
    .eq('id', surveyId)
    .maybeSingle();

  if (error) {
    console.error('Error checking survey completion:', error);
    throw error;
  }

  return !!data;
}

export async function saveSurveyResponse(userId: string, surveyId: string, response: object): Promise<void> {
  const { error } = await supabase
    .from('surveys')
    .insert({
      id: surveyId,
      user_id: userId,
      response: response,
    });

  if (error) {
    console.error('Error saving survey response:', error);
    throw error;
  }
}

// --- API Keys ---

export interface ApiKey {
  id: string;
  user_id: string;
  hashed_key: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
  description: string | null;
}

export async function getApiKeys(userId: string): Promise<ApiKey[]> {
  const { data, error } = await supabase
    .from('api_keys')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching API keys:', error);
    throw error;
  }

  return data || [];
}

export async function createApiKey(name: string): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('User not authenticated');
  }

  const accessToken = session.access_token;
  const backendUrl = process.env.REACT_APP_AI_BACKEND_URL;

  if (!backendUrl) {
    throw new Error('Backend URL is not configured');
  }

  const response = await fetch(`${backendUrl}/v1/user/api-key`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create API key');
  }

  const data = await response.json();
  return data.api_key;
}

export async function deleteApiKey(apiKeyId: string): Promise<void> {
  const { error } = await supabase
    .from('api_keys')
    .delete()
    .eq('id', apiKeyId);

  if (error) {
    console.error('Error deleting API key:', error);
    throw error;
  }
}